import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";
import { enqueueSnackbar } from "notistack";
import {
  downloadAssetImportTemplate,
  getAllAssetValuations,
  getAllValuers,
  getAssetImportSupportedCategories,
  importAssetsFromExcel,
} from "../../../../services/ValitanApiService";

const formatPersonName = (user) => {
  const parts = [user?.first_name || user?.firstName, user?.middle_name || user?.middleName, user?.last_name || user?.lastName]
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean);
  if (parts.length) return parts.join(" ");
  return user?.username || user?.email || String(user?.id || "Valuer");
};

const BRAND_BLUE = "#0147C8";
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const DEFAULT_CATEGORIES = [
  { value: "FURN", label: "Furniture & Fittings (FURN)" },
  { value: "ICT", label: "ICT Equipment (ICT)" },
  { value: "EQP", label: "Equipment (EQP)" },
];

const CATEGORY_LABELS = {
  FURN: "Furniture & Fittings",
  ICT: "ICT Equipment",
  EQP: "Equipment (General/Medical/Office)",
  LAND: "Land",
  BLDG: "Buildings",
  VEH: "Vehicles",
  MACH: "Machinery",
  SITE: "Site Works",
  INFR: "Infrastructure",
};

/** Hint files the user should upload per category */
const FILE_HINTS = {
  FURN: "FURNITURE 1.xlsx, furniture 2.xlsx",
  ICT: "ICT A.xlsx, ICT B.xlsx",
  EQP: "EQUIPMENT.xlsx",
};

const parseListPayload = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const extractErrorMessage = (error, fallback = "Request failed") => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  if (error?.message) return error.message;
  return fallback;
};

const downloadBlob = (blob, filename) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
};

/**
 * Category-specific Excel import UI (FURN / ICT / EQP survey workbooks).
 *
 * Props:
 * - isOpen, toggle (ignored when embedded=true)
 * - embedded: render as page panel instead of modal
 * - assetValuationId (optional if user should pick a valuation)
 * - valuationLabel (optional display)
 * - initialCategoryCode ("FURN" | "ICT" | "EQP" | …)
 * - lockCategory (hide category select when true)
 * - onImported() called after successful apply so parent can refresh
 */
const AssetImportModal = ({
  isOpen = true,
  toggle,
  embedded = false,
  assetValuationId = null,
  valuationLabel = "",
  initialCategoryCode = "FURN",
  lockCategory = false,
  onImported,
}) => {
  const active = embedded || isOpen;
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_CATEGORIES);
  const [categoryCode, setCategoryCode] = useState(
    String(initialCategoryCode || "FURN").toUpperCase()
  );
  const [selectedFile, setSelectedFile] = useState(null);
  // Default ON so valid rows can be imported while warnings/errors on other rows are skipped.
  const [allowPartial, setAllowPartial] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [preview, setPreview] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [valuationOptions, setValuationOptions] = useState([]);
  const [selectedValuation, setSelectedValuation] = useState(null);
  const [loadingValuations, setLoadingValuations] = useState(false);

  const [valuerOptions, setValuerOptions] = useState([]);
  const [selectedValuer, setSelectedValuer] = useState(null);
  const [loadingValuers, setLoadingValuers] = useState(false);

  const wasActiveRef = useRef(false);
  const needsValuationPicker = !assetValuationId;

  const resolvedValuationId = assetValuationId || selectedValuation?.value || null;
  const resolvedValuerId = selectedValuer?.value || null;

  const categorySelectValue = useMemo(
    () => categoryOptions.find((o) => o.value === categoryCode) || null,
    [categoryOptions, categoryCode]
  );

  // Reset form only when the UI newly opens — avoid wiping the partial-import toggle
  // on unrelated parent re-renders.
  useEffect(() => {
    if (!active) {
      wasActiveRef.current = false;
      return;
    }
    const justOpened = !wasActiveRef.current;
    wasActiveRef.current = true;
    if (!justOpened) return;

    setCategoryCode(String(initialCategoryCode || "FURN").toUpperCase());
    setSelectedFile(null);
    setPreview(null);
    setErrorMessage("");
    setAllowPartial(true);
    setBusy(false);
    setBusyAction("");
    setSelectedValuer(null);
    setSelectedValuation(null);
  }, [active, initialCategoryCode, assetValuationId]);

  // Keep category in sync when parent changes it while already open (locked tab).
  useEffect(() => {
    if (!active) return;
    setCategoryCode(String(initialCategoryCode || "FURN").toUpperCase());
  }, [active, initialCategoryCode]);

  useEffect(() => {
    if (!active) return;
    let mounted = true;
    (async () => {
      try {
        const res = await getAssetImportSupportedCategories();
        const codes = parseListPayload(res);
        if (!mounted || !Array.isArray(codes) || codes.length === 0) return;
        setCategoryOptions(
          codes.map((code) => {
            const c = String(code).toUpperCase();
            return {
              value: c,
              label: `${CATEGORY_LABELS[c] || c} (${c})`,
            };
          })
        );
      } catch {
        // keep defaults
      }
    })();
    return () => {
      mounted = false;
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;
    let mounted = true;
    (async () => {
      setLoadingValuations(true);
      try {
        const res = await getAllAssetValuations({ page: 0, size: 200 });
        const list = parseListPayload(res);
        if (!mounted) return;
        setValuationOptions(
          list.map((v) => {
            const id = v.id || v.asset_valuation_id;
            const ref = v.valuation_ref || v.valuationRef || id;
            const client =
              v.client_name ||
              v.clientName ||
              v.client?.name ||
              "";
            const valuerId = v.valuer_id || v.valuerId || null;
            return {
              value: id,
              label: client ? `${ref} — ${client}` : String(ref),
              valuerId,
            };
          })
        );
      } catch (err) {
        if (mounted) {
          enqueueSnackbar(extractErrorMessage(err, "Failed to load valuations"), {
            variant: "error",
          });
        }
      } finally {
        if (mounted) setLoadingValuations(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;
    let mounted = true;
    (async () => {
      setLoadingValuers(true);
      try {
        const res = await getAllValuers(true);
        const list = parseListPayload(res);
        if (!mounted) return;
        const options = list.map((u) => ({
          value: u.id,
          label: formatPersonName(u),
        }));
        setValuerOptions(options);
      } catch (err) {
        if (mounted) {
          enqueueSnackbar(extractErrorMessage(err, "Failed to load valuers"), {
            variant: "error",
          });
        }
      } finally {
        if (mounted) setLoadingValuers(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [active]);

  // Prefill valuer from the selected (or locked) valuation when available.
  useEffect(() => {
    if (!active || !valuerOptions.length) return;
    const fromPicker = selectedValuation?.valuerId;
    const lockedValuation = !needsValuationPicker
      ? valuationOptions.find((o) => o.value === assetValuationId)
      : null;
    const preferred = fromPicker || lockedValuation?.valuerId || null;
    if (!preferred) return;
    const match = valuerOptions.find((o) => String(o.value) === String(preferred));
    if (match) {
      setSelectedValuer(match);
    }
  }, [
    active,
    valuerOptions,
    selectedValuation,
    needsValuationPicker,
    assetValuationId,
    valuationOptions,
  ]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setPreview(null);
    setErrorMessage("");
  };

  const validateReady = useCallback(() => {
    if (!resolvedValuationId) {
      setErrorMessage("Select a valuation to import into.");
      return false;
    }
    if (!resolvedValuerId) {
      setErrorMessage("Select a valuer.");
      return false;
    }
    if (!categoryCode) {
      setErrorMessage("Select an asset category.");
      return false;
    }
    if (!selectedFile) {
      setErrorMessage("Choose an Excel file (.xlsx or .xls).");
      return false;
    }
    if (selectedFile.size > MAX_FILE_BYTES) {
      setErrorMessage("File exceeds the 10MB limit.");
      return false;
    }
    const name = (selectedFile.name || "").toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      setErrorMessage("Only .xlsx or .xls files are supported.");
      return false;
    }
    return true;
  }, [resolvedValuationId, resolvedValuerId, categoryCode, selectedFile]);

  const runImport = async (dryRun) => {
    if (!validateReady()) return;
    setBusy(true);
    setBusyAction(dryRun ? "preview" : "apply");
    setErrorMessage("");
    try {
      const response = await importAssetsFromExcel({
        file: selectedFile,
        assetCategoryCode: categoryCode,
        assetValuationId: resolvedValuationId,
        valuerId: resolvedValuerId,
        dryRun,
        allowPartial,
      });
      const body = response?.data || {};
      if (body.status === "failed") {
        setErrorMessage(body.message || "Import failed");
        setPreview(body.data || null);
        return;
      }
      const data = body.data || body;
      setPreview(data);
      if (dryRun) {
        enqueueSnackbar(
          `Preview: ${data.valid_rows ?? data.validRows ?? 0} valid of ${data.total_rows ?? data.totalRows ?? 0} rows`,
          { variant: "info" }
        );
      } else {
        const inserted = data.inserted ?? 0;
        enqueueSnackbar(`Imported ${inserted} asset(s) successfully.`, {
          variant: "success",
        });
        if (typeof onImported === "function") {
          onImported(data);
        }
      }
    } catch (err) {
      const msg = extractErrorMessage(err, "Import failed");
      setErrorMessage(msg);
      const maybeData = err?.response?.data?.data;
      if (maybeData) setPreview(maybeData);
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setBusy(false);
      setBusyAction("");
    }
  };

  const handleDownloadTemplate = async () => {
    if (!categoryCode) {
      enqueueSnackbar("Select a category first.", { variant: "warning" });
      return;
    }
    setBusy(true);
    setBusyAction("template");
    try {
      const response = await downloadAssetImportTemplate(categoryCode);
      const blob = response?.data;
      if (!blob) throw new Error("Empty template response");
      downloadBlob(blob, `asset_import_template_${categoryCode}.xlsx`);
      enqueueSnackbar("Template downloaded.", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err, "Failed to download template"), {
        variant: "error",
      });
    } finally {
      setBusy(false);
      setBusyAction("");
    }
  };

  const totalRows = preview?.total_rows ?? preview?.totalRows ?? 0;
  const validRows = preview?.valid_rows ?? preview?.validRows ?? 0;
  const errorRows = preview?.error_rows ?? preview?.errorRows ?? 0;
  const warningRows = preview?.warning_rows ?? preview?.warningRows ?? 0;
  const inserted = preview?.inserted ?? 0;
  const issues = Array.isArray(preview?.issues) ? preview.issues : [];
  const sampleNames = Array.isArray(preview?.sample_names)
    ? preview.sample_names
    : Array.isArray(preview?.sampleNames)
      ? preview.sampleNames
      : [];
  const isDryRunPreview =
    !!preview &&
    (preview.dry_run === true ||
      preview.dryRun === true ||
      preview.dry_run === "true" ||
      preview.dryRun === "true");
  const canApply =
    isDryRunPreview && validRows > 0 && (errorRows === 0 || allowPartial);

  const partialCheckboxId = embedded ? "allowPartialImportPage" : "allowPartialImportModal";

  const formBody = (
    <>
      <p className="text-muted small mb-3">
        Upload a category-specific Excel file. Preview first (dry-run), then apply to insert
        assets into the selected valuation. Supported:{" "}
        <strong>FURN</strong>, <strong>ICT</strong>, <strong>EQP</strong>.
        {FILE_HINTS[categoryCode] ? (
          <>
            {" "}
            Expected files for this category: <em>{FILE_HINTS[categoryCode]}</em>.
          </>
        ) : null}
      </p>

      <Alert color="info" className="py-2 small">
        <div className="fw-semibold mb-1">Survey files</div>
        <ul className="mb-0 ps-3">
          <li>
            <strong>FURN</strong> — FURNITURE 1.xlsx, furniture 2.xlsx
          </li>
          <li>
            <strong>ICT</strong> — ICT A.xlsx, ICT B.xlsx
          </li>
          <li>
            <strong>EQP</strong> — EQUIPMENT.xlsx
          </li>
        </ul>
      </Alert>

      {errorMessage && (
        <Alert color="danger" className="py-2">
          {errorMessage}
        </Alert>
      )}

      <Row className="g-3">
        <Col md={6}>
          <FormGroup>
            <Label className="fw-semibold">Category</Label>
            {lockCategory ? (
              <Input
                plaintext
                readOnly
                value={categorySelectValue?.label || categoryCode}
                className="fw-bold"
              />
            ) : (
              <Select
                options={categoryOptions}
                value={categorySelectValue}
                onChange={(opt) => {
                  setCategoryCode(opt?.value || "");
                  setPreview(null);
                }}
                isDisabled={busy}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label className="fw-semibold">Valuation</Label>
            {needsValuationPicker ? (
              <Select
                options={valuationOptions}
                value={selectedValuation}
                onChange={(opt) => {
                  setSelectedValuation(opt);
                  setPreview(null);
                  if (opt?.valuerId) {
                    const match = valuerOptions.find(
                      (o) => String(o.value) === String(opt.valuerId)
                    );
                    if (match) setSelectedValuer(match);
                  }
                }}
                isLoading={loadingValuations}
                isDisabled={busy || loadingValuations}
                placeholder="Select valuation…"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            ) : (
              <Input
                plaintext
                readOnly
                value={valuationLabel || resolvedValuationId}
                className="fw-bold"
              />
            )}
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <Label className="fw-semibold">Valuer</Label>
            <Select
              options={valuerOptions}
              value={selectedValuer}
              onChange={(opt) => {
                setSelectedValuer(opt);
                setPreview(null);
              }}
              isLoading={loadingValuers}
              isDisabled={busy || loadingValuers}
              placeholder="Select valuer…"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
            <small className="text-muted">
              Assigned on the valuation (same as LAND/INFR imports). Assets link via valuation id.
            </small>
          </FormGroup>
        </Col>
      </Row>

      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <Button
          color="light"
          className="border"
          size="sm"
          onClick={handleDownloadTemplate}
          disabled={busy || !categoryCode}
        >
          {busy && busyAction === "template" ? (
            <Spinner size="sm" className="me-1" />
          ) : (
            <i className="ri-download-line me-1" />
          )}
          Download blank template
        </Button>
        <span className="text-muted small">
          Or upload the field survey workbook for the selected category.
        </span>
      </div>

      <FormGroup>
        <Label className="fw-semibold">Excel file</Label>
        <Input
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={handleFileChange}
          disabled={busy}
        />
        {selectedFile && (
          <small className="text-muted d-block mt-1">
            {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
          </small>
        )}
      </FormGroup>

      <div
        className="d-flex align-items-start gap-2 mb-3 p-2 border rounded"
        style={{
          backgroundColor: allowPartial ? "rgba(1, 71, 200, 0.06)" : "#fff",
          cursor: busy ? "not-allowed" : "pointer",
        }}
        onClick={() => {
          if (!busy) setAllowPartial((prev) => !prev);
        }}
        role="checkbox"
        aria-checked={allowPartial}
        aria-disabled={busy}
        tabIndex={0}
        onKeyDown={(e) => {
          if (busy) return;
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            setAllowPartial((prev) => !prev);
          }
        }}
      >
        <input
          className="form-check-input mt-1 flex-shrink-0"
          type="checkbox"
          id={partialCheckboxId}
          checked={allowPartial}
          onChange={(e) => {
            e.stopPropagation();
            setAllowPartial(Boolean(e.target.checked));
          }}
          onClick={(e) => e.stopPropagation()}
          disabled={busy}
          style={{ width: "1.15rem", height: "1.15rem", cursor: busy ? "not-allowed" : "pointer" }}
        />
        <div>
          <label
            className="form-check-label fw-semibold mb-0"
            htmlFor={partialCheckboxId}
            onClick={(e) => e.stopPropagation()}
            style={{ cursor: busy ? "not-allowed" : "pointer" }}
          >
            Allow partial import{" "}
            <Badge color={allowPartial ? "success" : "secondary"} pill className="ms-1">
              {allowPartial ? "ON" : "OFF"}
            </Badge>
          </label>
          <div className="small text-muted">
            Insert valid rows even if some rows have errors. Warnings never block import.
          </div>
        </div>
      </div>

      {preview && (
        <div className="border rounded p-3 mb-2 bg-light">
          <div className="d-flex flex-wrap gap-2 mb-2">
            <Badge color="secondary" pill>
              Total: {totalRows}
            </Badge>
            <Badge color="success" pill>
              Valid: {validRows}
            </Badge>
            <Badge color="danger" pill>
              Errors: {errorRows}
            </Badge>
            <Badge color="warning" pill>
              Warnings: {warningRows}
            </Badge>
            {(preview.dry_run === false || preview.dryRun === false) && (
              <Badge color="primary" pill>
                Inserted: {inserted}
              </Badge>
            )}
          </div>
          {sampleNames.length > 0 && (
            <div className="small text-muted mb-2">
              Sample: {sampleNames.slice(0, 5).join(", ")}
              {sampleNames.length > 5 ? "…" : ""}
            </div>
          )}
          {issues.length > 0 && (
            <div style={{ maxHeight: embedded ? 360 : 220, overflowY: "auto" }}>
              <Table size="sm" bordered hover className="mb-0 bg-white">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Row</th>
                    <th style={{ width: 90 }}>Severity</th>
                    <th style={{ width: 120 }}>Field</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.slice(0, 100).map((issue, idx) => (
                    <tr key={`${issue.row}-${issue.field}-${idx}`}>
                      <td>{issue.row ?? "—"}</td>
                      <td>
                        <Badge
                          color={
                            String(issue.severity).toUpperCase() === "ERROR"
                              ? "danger"
                              : "warning"
                          }
                        >
                          {issue.severity || "INFO"}
                        </Badge>
                      </td>
                      <td>{issue.field || "—"}</td>
                      <td>
                        {issue.message}
                        {issue.asset_name || issue.assetName ? (
                          <span className="text-muted">
                            {" "}
                            ({issue.asset_name || issue.assetName})
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {issues.length > 100 && (
                <small className="text-muted">Showing first 100 of {issues.length} issues.</small>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  const actionButtons = (
    <div className="d-flex gap-2">
      <Button
        color="info"
        outline
        onClick={() => runImport(true)}
        disabled={busy || !selectedFile}
      >
        {busy && busyAction === "preview" ? (
          <Spinner size="sm" className="me-1" />
        ) : (
          <i className="ri-eye-line me-1" />
        )}
        Preview (dry-run)
      </Button>
      <Button
        style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
        onClick={() => runImport(false)}
        disabled={busy || !canApply}
        title={
          canApply
            ? "Insert valid rows"
            : "Run a successful preview first (and fix errors unless partial is allowed)"
        }
      >
        {busy && busyAction === "apply" ? (
          <Spinner size="sm" className="me-1" />
        ) : (
          <i className="ri-upload-cloud-2-line me-1" />
        )}
        Apply import
      </Button>
    </div>
  );

  if (embedded) {
    return (
      <div>
        {formBody}
        <div className="d-flex justify-content-end mt-3">{actionButtons}</div>
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={busy ? undefined : toggle} size="lg" backdrop="static">
      <ModalHeader toggle={busy ? undefined : toggle}>
        <i className="ri-upload-2-line me-2" style={{ color: BRAND_BLUE }} />
        Import assets from Excel
      </ModalHeader>
      <ModalBody>{formBody}</ModalBody>
      <ModalFooter className="d-flex justify-content-between flex-wrap gap-2">
        <Button color="light" onClick={toggle} disabled={busy}>
          Close
        </Button>
        {actionButtons}
      </ModalFooter>
    </Modal>
  );
};

export default AssetImportModal;
